import React, { FC, memo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useI18n, useSelector } from '~Hooks';
import { BlueButton, Box, Button, OutlinedButton, TransparentButton } from '~Styles';
import { ModalContent } from '~Styles/modal';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import Icon from '~ComponentsImproved/Icon';
import FiltersIconSrc from '~Assets/icons/filter.svg';
import PaymentsIconSrc from '~Assets/icons/payments.svg';
import StaffIconSrc from '~Assets/icons/person.svg';
import DiscountIconSrc from '~Assets/icons/percent_discount.svg';
import RangeFilter from './RangeFilter';
import SearchSelect from '~ComponentsImproved/SearchSelect';
import useOffers from '~Hooks/useOffers';
import { methodToIconMap, GeneralPaymentMethodIcon } from '~Components/PayWithButton';
import { useStorage } from '~Components/Storage';

const OpenFiltersButton = styled(TransparentButton)`
  display: flex;
  align-items: center;
  min-width: 58px;
  max-width: 58px;
  padding: 0px;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const FilterBadge = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  background-color: var(--colorBlueGray900);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--colorWhite);
  pointer-events: none;
`;

const FilterButton = styled(OutlinedButton)<{ isSelected?: boolean }>`
  /** Highlight the background when this filter has a selected value */
  background-color: ${({ isSelected }) =>
    isSelected ? 'var(--colorBlueGray1100)' : 'transparent'};
  color: var(--colorWhite);
  /** Smaller, non-uppercase text so the label (or selected name) fits the narrow button */
  font-size: 11px;
  text-transform: none;
  letter-spacing: normal;
  min-width: 0;
  padding-left: 8px;
  padding-right: 8px;

  &:active {
    background-color: var(--colorBlueGray1100);
  }
`;

/** Truncate long selected names with an ellipsis instead of overflowing the button */
const ButtonLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface AdvancedFilterValues {
  amountMin: string;
  amountMax: string;
  discountMin: string;
  discountMax: string;
  tipMin: string;
  tipMax: string;
  hasDiscount: boolean;
  hasTip: boolean;
  /** Selected staff id ('' = none) */
  staffId: string;
  /** Selected payment method id ('' = none) */
  paymentMethod: string;
  /** Selected discount/offer id ('' = none) */
  discountId: string;
}

export const DEFAULT_ADVANCED_FILTERS: AdvancedFilterValues = {
  amountMin: '',
  amountMax: '',
  discountMin: '',
  discountMax: '',
  tipMin: '',
  tipMax: '',
  hasDiscount: false,
  hasTip: false,
  staffId: '',
  paymentMethod: '',
  discountId: '',
};

const Wrapper = styled(ModalContent)`
  max-width: 480px;

  /**
   * Mirrors the showCreateUser ModalContent styling in LinkUser so the Android
   * keyboard (which shrinks the viewport height) doesn't cover the inputs:
   * cap to the visible height and scroll on short viewports, then relax and
   * push the modal up once there is room.
   */
  height: fit-content;
  max-height: calc(100vh - 16px);
  overflow-y: auto;

  @media (min-height: 496px) {
    max-height: none;
    overflow-y: visible;
    margin: 16px 0 0 0;
  }

  @media (min-height: 680px) {
    margin: 16px 0 10% 0;
  }
`;

/** Lets each toggle take an equal share when placed on the same row */
const ToggleItem = styled.div`
  flex: 1;
`;

interface Props {
  filters: AdvancedFilterValues;
  onChange: (filters: AdvancedFilterValues) => void;
}

/**
 * Advanced order filters, presented in a modal triggered from the orders list.
 * Provides min/max ranges for amount, discount and tip plus hasDiscount / hasTip toggles.
 * Fully controlled, the parent owns the filter state and feeds it into the query.
 */
const AdvancedFilters: FC<Props> = ({ filters, onChange }) => {
  const { i18n } = useI18n();
  const modal = useModal();
  const [storage] = useStorage();
  const { offers } = useOffers();

  const venueId = useSelector((state) => state.venueId) as string;
  const companyStaff = useSelector((state) => state.companyStaff)?.staffs;
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);

  /** Only payment methods available at the current venue (or global, with no venues set) */
  const venuePaymentMethods = paymentMethods.filter(
    (pm) => pm.venues.length === 0 || pm.venues.some((v) => v === venueId),
  );

  /** Local draft, only committed to the parent when the user presses Save */
  const [draft, setDraft] = useState<AdvancedFilterValues>(filters);

  /** Re-sync the draft with the applied filters whenever the modal opens */
  useEffect(() => {
    if (modal.isShowing) {
      setDraft(filters);
    }
    // Only re-sync on open, not on every applied-filter change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.isShowing]);

  const filterButton = useButton({ onClick: modal.toggle });
  const saveButton = useButton({
    onClick: () => {
      onChange(draft);
      modal.toggle();
    },
  });
  const clearButton = useButton({
    onClick: () => {
      setDraft(DEFAULT_ADVANCED_FILTERS);
      onChange(DEFAULT_ADVANCED_FILTERS);
      modal.toggle();
    },
  });

  /** Staff / payment method / discount pickers open their own SelectOptionModal */
  const [showStaffSelect, setShowStaffSelect] = useState(false);
  const [showPaymentSelect, setShowPaymentSelect] = useState(false);
  const [showDiscountSelect, setShowDiscountSelect] = useState(false);

  const selectStaffButton = useButton({ onClick: () => setShowStaffSelect(true) });
  const selectPaymentButton = useButton({ onClick: () => setShowPaymentSelect(true) });
  const selectDiscountButton = useButton({ onClick: () => setShowDiscountSelect(true) });

  /** Names of the currently selected options, shown on their buttons when set */
  const selectedStaffName = companyStaff?.find((s) => s.id === draft.staffId)?.name;
  const selectedPaymentName = paymentMethods.find((m) => m.id === draft.paymentMethod)?.title;
  const selectedDiscountName = offers.find((o) => o.id === draft.discountId)?.name;

  /**
   * Count active filters (applied, not draft) to surface on the trigger button.
   * A min/max range counts as a single filter even when both bounds are set.
   */
  const activeCount =
    (filters.amountMin || filters.amountMax ? 1 : 0) +
    (filters.discountMin || filters.discountMax ? 1 : 0) +
    (filters.tipMin || filters.tipMax ? 1 : 0) +
    (filters.hasDiscount ? 1 : 0) +
    (filters.hasTip ? 1 : 0) +
    (filters.staffId ? 1 : 0) +
    (filters.paymentMethod ? 1 : 0) +
    (filters.discountId ? 1 : 0);

  /** Update a single field in the local draft */
  const updateField = (key: keyof AdvancedFilterValues, value: string | boolean) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const isHandy = window.innerWidth <= 600;

  const showStaffFilter = companyStaff && companyStaff.length > 0;
  const showPaymentFilter = venuePaymentMethods && venuePaymentMethods.length > 1;
  const showDiscountFilter = offers && offers.length > 0;

  const showNoButtonFilters = !showStaffFilter && !showPaymentFilter && !showDiscountFilter;

  return (
    <>
      <ButtonWrapper>
        <OpenFiltersButton fluid {...filterButton}>
          <Icon height="24px" width="24px" src={FiltersIconSrc} color="var(--colorBlueGray200)" />
        </OpenFiltersButton>
        {activeCount > 0 && <FilterBadge>{activeCount}</FilterBadge>}
      </ButtonWrapper>

      <Modal isShowing={modal.isShowing} hide={modal.toggle}>
        <Wrapper display="flex" flexDirection="column" gap={isHandy ? 'small' : 'medium'}>
          {!isHandy && (
            <Heading level={3} color="var(--colorWhite)" bottom={0}>
              {i18n('History.AdvancedFilters')}
            </Heading>
          )}

          <RangeFilter
            label={i18n('History.Amount')}
            minValue={draft.amountMin}
            maxValue={draft.amountMax}
            onMinChange={(value) => updateField('amountMin', value)}
            onMaxChange={(value) => updateField('amountMax', value)}
          />
          <RangeFilter
            label={i18n('History.Discount')}
            minValue={draft.discountMin}
            maxValue={draft.discountMax}
            onMinChange={(value) => updateField('discountMin', value)}
            onMaxChange={(value) => updateField('discountMax', value)}
          />
          {storage.enableTipping && (
            <RangeFilter
              label={i18n('History.Tip')}
              minValue={draft.tipMin}
              maxValue={draft.tipMax}
              onMinChange={(value) => updateField('tipMin', value)}
              onMaxChange={(value) => updateField('tipMax', value)}
            />
          )}

          <ToggleItem>
            <Toggle
              /** Key on the value so a Clear (draft reset) resets the toggle's internal state */
              key={`hasDiscount-${draft.hasDiscount}`}
              lightText
              fullWidth
              initialValue={draft.hasDiscount}
              label={i18n('History.HasDiscount')}
              onToggle={(isActive) => updateField('hasDiscount', isActive)}
            />
          </ToggleItem>
          {storage.enableTipping && (
            <ToggleItem>
              <Toggle
                key={`hasTip-${draft.hasTip}`}
                lightText
                fullWidth
                initialValue={draft.hasTip}
                label={i18n('History.HasTip')}
                onToggle={(isActive) => updateField('hasTip', isActive)}
              />
            </ToggleItem>
          )}

          {!showNoButtonFilters && (
            <Box display="flex" gap={isHandy ? 'xs' : 'medium'} fluid>
              {showStaffFilter && (
                <FilterButton
                  fluid
                  size="small"
                  display="flex"
                  alignItems="center"
                  gap="xs"
                  isSelected={!!draft.staffId}
                  {...selectStaffButton}>
                  <Icon
                    src={StaffIconSrc}
                    color="var(--colorBlueGray200)"
                    height="20px"
                    width="20px"
                    style={{ flexShrink: 0 }}
                  />
                  <ButtonLabel>{selectedStaffName ?? i18n('History.Staff')}</ButtonLabel>
                </FilterButton>
              )}

              {showPaymentFilter && (
                <FilterButton
                  fluid
                  size="small"
                  display="flex"
                  alignItems="center"
                  gap="xs"
                  isSelected={!!draft.paymentMethod}
                  {...selectPaymentButton}>
                  <Icon
                    src={PaymentsIconSrc}
                    color="var(--colorBlueGray200)"
                    height="20px"
                    width="20px"
                    style={{ flexShrink: 0 }}
                  />
                  <ButtonLabel>{selectedPaymentName ?? i18n('History.PaymentMethod')}</ButtonLabel>
                </FilterButton>
              )}

              {showDiscountFilter && (
                <FilterButton
                  fluid
                  size="small"
                  display="flex"
                  alignItems="center"
                  gap="xs"
                  isSelected={!!draft.discountId}
                  {...selectDiscountButton}>
                  <Icon
                    src={DiscountIconSrc}
                    color="var(--colorBlueGray200)"
                    height="20px"
                    width="20px"
                    style={{ flexShrink: 0 }}
                  />
                  <ButtonLabel>{selectedDiscountName ?? i18n('History.Discount')}</ButtonLabel>
                </FilterButton>
              )}
            </Box>
          )}

          <Box display="flex" gap="small" justifyContent="space-between">
            <Button {...clearButton}>{i18n('General.Clear')}</Button>
            <BlueButton {...saveButton} fluid>
              {i18n('General.Submit')}
            </BlueButton>
          </Box>
        </Wrapper>
      </Modal>

      {showStaffSelect && (
        <SearchSelect
          title={i18n('History.SelectStaff')}
          selectedId={draft.staffId}
          options={(companyStaff ?? []).map((s) => ({ id: s.id, label: s.name }))}
          onSelect={(id) => {
            updateField('staffId', id);
            setShowStaffSelect(false);
          }}
          onClear={() => {
            updateField('staffId', '');
            setShowStaffSelect(false);
          }}
          onClose={() => setShowStaffSelect(false)}
        />
      )}

      {showPaymentSelect && (
        <SearchSelect
          title={i18n('History.SelectPaymentMethod')}
          selectedId={draft.paymentMethod}
          options={venuePaymentMethods.map((m) => ({
            id: m.id,
            label: m.title,
            icon: methodToIconMap[m.id] || GeneralPaymentMethodIcon,
          }))}
          onSelect={(id) => {
            updateField('paymentMethod', id);
            setShowPaymentSelect(false);
          }}
          onClear={() => {
            updateField('paymentMethod', '');
            setShowPaymentSelect(false);
          }}
          onClose={() => setShowPaymentSelect(false)}
        />
      )}

      {showDiscountSelect && (
        <SearchSelect
          title={i18n('History.SelectDiscount')}
          selectedId={draft.discountId}
          options={offers.map((o) => ({
            id: o.id,
            label: i18n(o.nameLang),
          }))}
          onSelect={(id) => {
            updateField('discountId', id);
            setShowDiscountSelect(false);
          }}
          onClear={() => {
            updateField('discountId', '');
            setShowDiscountSelect(false);
          }}
          onClose={() => setShowDiscountSelect(false)}
        />
      )}
    </>
  );
};

export default memo(AdvancedFilters);
